import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { getPaginationData } from '../../utils/table-functions';
import { Span } from '../typography/typography-component';
import {
  PaginationFirst,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from './pagination-icons-component';
import Styled from './pagination-style';

/**
 * Pagination component for generic use in project.
 *
 * @param {object} props - Props of the component.
 * @param {Function} props.setPageIndex - Function to call when the page index is changed.
 * @param {Function} props.setPageSize - Function to call when the page size is changed.
 * @param {number} props.pageSize - Size of the page.
 * @param {Array} props.pageSizes - Sizes of the page.
 * @param {string} props.tableTitle - Title of the table.
 * @param {number} props.pageIndex - Index of the page.
 * @param {number} props.dataCount - Count of the data.
 * @returns {JSX.Element}
 */
const Pagination = ({
  setPageIndex,
  setPageSize,
  pageSize,
  pageSizes,
  tableTitle,
  pageIndex,
  dataCount,
}) => {
  const theme = useTheme();
  const paginationData = getPaginationData({
    pageIndex,
    pageSize,
    dataCount,
  });

  const pageSelectionStyle = {
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
  };

  const paginationTextStyle = { marginRight: 32 };

  return (
    <Styled.PaginationContainer>
      <Span htmlFor="pageSelection" style={pageSelectionStyle}>
        Rows per page
        <Styled.PageSelect
          id="pageSelection"
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPageIndex(0);
          }}
        >
          {pageSizes.map((pageSize_) => (
            <option
              key={`pageindex-${tableTitle}-${pageSize_}`}
              value={pageSize_}
            >
              {pageSize_}
            </option>
          ))}
        </Styled.PageSelect>
      </Span>
      <Span style={paginationTextStyle}>{paginationData.paginationText}</Span>
      <Styled.PaginationBtnContainer
        onClick={() => {
          if (pageIndex !== 0) {
            setPageIndex(0);
          }
        }}
        disabled={!paginationData.canPrevious}
      >
        <PaginationFirst
          color={
            paginationData.canPrevious
              ? theme.COLOR_TABLE_DARK
              : theme.COLOR_GREY4
          }
        />
      </Styled.PaginationBtnContainer>
      <Styled.PaginationBtnContainer
        onClick={() => {
          if (pageIndex !== 0) {
            setPageIndex(pageIndex - 1);
          }
        }}
        disabled={pageIndex === 0}
      >
        <PaginationPrevious
          color={
            paginationData.canPrevious
              ? theme.COLOR_TABLE_DARK
              : theme.COLOR_GREY4
          }
        />
      </Styled.PaginationBtnContainer>
      <Styled.PaginationBtnContainer
        onClick={() => {
          if (paginationData.canNext) {
            setPageIndex(pageIndex + 1);
          }
        }}
        disabled={!paginationData.canNext}
      >
        <PaginationNext
          color={
            paginationData.canNext ? theme.COLOR_TABLE_DARK : theme.COLOR_GREY4
          }
        />
      </Styled.PaginationBtnContainer>
      <Styled.PaginationBtnContainer
        onClick={() => {
          if (paginationData.canNext) {
            setPageIndex(paginationData.lastPage);
          }
        }}
        disabled={!paginationData.canNext}
      >
        <PaginationLast
          color={
            paginationData.canNext ? theme.COLOR_TABLE_DARK : theme.COLOR_GREY4
          }
        />
      </Styled.PaginationBtnContainer>
    </Styled.PaginationContainer>
  );
};

Pagination.propTypes = {
  setPageIndex: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageSizes: PropTypes.arrayOf(PropTypes.number).isRequired,
  tableTitle: PropTypes.string.isRequired,
  pageIndex: PropTypes.number.isRequired,
  dataCount: PropTypes.number.isRequired,
};

export default Pagination;
